import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isNoop, useBrainApi } from "../../api/brainApi";
import { Domain, TranscriptSummary } from "../../api/types";
import {
  PageTitle,
  Panel,
  AlertBanner,
  selectClass,
  btnPrimary,
  btnSecondary,
} from "../../components/controlPlaneUi";
import Modal from "../../components/Modal";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT_EXT = [".txt", ".md", ".markdown"];

const isAccepted = (f: File) =>
  ACCEPT_EXT.some((ext) => f.name.toLowerCase().endsWith(ext));

const stageKey = (f: File) => `${f.name}:${f.size}`;

const UploadPage: React.FC = () => {
  const api = useBrainApi();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [domainId, setDomainId] = useState<number | "">("");
  const [transcripts, setTranscripts] = useState<TranscriptSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<React.ReactNode>(null);
  const [triggering, setTriggering] = useState(false);
  const [staged, setStaged] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [noopMessage, setNoopMessage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .listDomains()
      .then((ds) => {
        setDomains(ds);
        setDomainId((cur) => (cur === "" && ds.length ? ds[0].id : cur));
      })
      .catch((e) => setError(e instanceof Error ? e.message : "failed to load domains"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const loadTranscripts = useCallback(
    async (id: number) => {
      try {
        setTranscripts(await api.listTranscripts(id));
      } catch (e) {
        setError(e instanceof Error ? e.message : "failed to load transcripts");
      }
    },
    [api]
  );

  useEffect(() => {
    if (typeof domainId === "number") void loadTranscripts(domainId);
  }, [domainId, loadTranscripts]);

  // Validate + stage files coming from either the picker or a drop.
  const addFiles = useCallback((files: FileList | File[]) => {
    setError(null);
    setNotice(null);
    const good: File[] = [];
    const bad: string[] = [];
    for (const f of Array.from(files)) {
      if (!isAccepted(f)) bad.push(`${f.name} (unsupported type)`);
      else if (f.size > MAX_BYTES) bad.push(`${f.name} (over 10 MB)`);
      else good.push(f);
    }
    if (bad.length) setError(`Skipped — ${bad.join(", ")}`);
    if (good.length) {
      setStaged((cur) => {
        const seen = new Set(cur.map(stageKey));
        return [...cur, ...good.filter((f) => !seen.has(stageKey(f)))];
      });
    }
  }, []);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };

  // Only clear the highlight when the pointer leaves the dropzone itself,
  // not when it moves over a child element (avoids flicker).
  const onDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setDragging(false);
    }
  };

  const removeStaged = (idx: number) =>
    setStaged((cur) => cur.filter((_, i) => i !== idx));

  // Push the staged files to the domain's raw/ (the run ingests from there). Clears staging.
  const uploadStagedFiles = async (id: number) => {
    const ok: string[] = [];
    const failed: string[] = [];
    for (const f of staged) {
      try {
        const t = await api.uploadTranscript(id, f);
        ok.push(t.fileName);
      } catch (e) {
        failed.push(`${f.name} (${e instanceof Error ? e.message : "failed"})`);
      }
    }
    setStaged([]);
    if (fileRef.current) fileRef.current.value = "";
    await loadTranscripts(id);
    return { ok, failed };
  };

  const selectedDomain = domains.find((d) => d.id === domainId);

  // Primary action: upload the staged transcripts, then run ingestion on them. With nothing
  // staged it still runs (re-ingests whatever is already in raw/, plus the code baseline).
  const triggerIngestion = async () => {
    if (!selectedDomain || typeof domainId !== "number") return;
    setError(null);
    setNotice(null);
    setTriggering(true);
    try {
      let uploaded = 0;
      if (staged.length) {
        const { ok, failed } = await uploadStagedFiles(domainId);
        uploaded = ok.length;
        if (failed.length) {
          setError(`Some files failed to upload — ${failed.join(", ")}. Run not started; fix and retry.`);
          return;
        }
      }
      const res = await api.triggerRun(selectedDomain.name);
      if (isNoop(res)) {
        setNoopMessage(res.reason);
        return;
      }
      setNotice(
        <>
          {uploaded > 0
            ? `Uploaded ${uploaded} file${uploaded > 1 ? "s" : ""} and started ingestion`
            : "Started ingestion"}{" "}
          for "{selectedDomain.name}" — transcripts + code/docs.{" "}
          <Link to="/console/runs" className="font-medium underline">
            View on Jobs →
          </Link>
        </>
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "trigger failed");
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div>
      <PageTitle kind="Ingestion" name="upload-transcripts" />
      {error && <AlertBanner variant="error">{error}</AlertBanner>}
      {notice && <AlertBanner variant="ok">{notice}</AlertBanner>}
      {noopMessage && (
        <Modal title="Nothing to run" onClose={() => setNoopMessage(null)}>
          {noopMessage}
        </Modal>
      )}

      {domains.length === 0 ? (
        <Panel title="No domains">
          <p className="text-sm text-slate-600">
            Create a domain first on the <span className="font-mono">Domains</span> page, then
            upload transcripts to it.
          </p>
        </Panel>
      ) : (
        <div className="space-y-4">
          {/* Upload functions — across the top */}
          <Panel title="Upload transcripts">
            <div className="grid gap-4 md:grid-cols-[260px_1fr] md:items-start">
              {/* Domain + ingestion trigger */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Domain</label>
                  <select
                    className={selectClass}
                    value={domainId}
                    onChange={(e) => setDomainId(e.target.value ? Number(e.target.value) : "")}
                  >
                    {domains.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pt-1">
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={triggering || typeof domainId !== "number"}
                    onClick={triggerIngestion}
                  >
                    {triggering
                      ? "Working…"
                      : staged.length > 0
                        ? `Upload ${staged.length} file${staged.length > 1 ? "s" : ""} & run ingestion`
                        : "Trigger ingestion run"}
                  </button>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Drop transcripts on the right, then run — the staged files are uploaded and
                    ingested. With none staged it re-ingests this domain's existing files (plus the
                    code baseline). Track status on{" "}
                    <Link to="/console/runs" className="text-[#C9A260] hover:underline">
                      Past Runs
                    </Link>
                    .
                  </p>
                </div>
              </div>

              {/* Drag-and-drop zone */}
              <div>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Choose or drop transcript files"
                  onClick={() => fileRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      fileRef.current?.click();
                    }
                  }}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  className={`flex flex-col items-center justify-center gap-1.5 px-4 py-8 border-2 border-dashed rounded-sm text-center cursor-pointer transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[#C9A260] ${
                    dragging
                      ? "border-[#C9A260] bg-[#C9A260]/5"
                      : "border-[#262420] hover:border-[#9D9585] bg-[#141210]/50"
                  }`}
                >
                  <svg
                    className={`h-7 w-7 ${dragging ? "text-[#C9A260]" : "text-[#5A5448]"}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M12 16V4" />
                    <path d="M7 9l5-5 5 5" />
                    <path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
                  </svg>
                  <div className="text-sm text-[#9D9585]">
                    <span className="font-medium text-[#C9A260]">Click to choose</span> or drag
                    &amp; drop
                  </div>
                  <div className="text-[11px] text-slate-400">
                    .txt / .md / .markdown · ≤ 10 MB each · multiple allowed
                  </div>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept=".txt,.md,.markdown,text/plain,text/markdown"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length) addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />

                {staged.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                      Staged — uploaded &amp; ingested when you run ({staged.length})
                    </div>
                    {staged.map((f, i) => (
                      <div
                        key={`${stageKey(f)}:${i}`}
                        className="flex items-center justify-between gap-2 text-sm border border-[#262420] bg-[#141210] px-2.5 py-1.5"
                      >
                        <span className="font-mono truncate">{f.name}</span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[11px] text-slate-400 tabular-nums">
                            {(f.size / 1024).toFixed(1)} KB
                          </span>
                          <button
                            type="button"
                            onClick={() => removeStaged(i)}
                            disabled={triggering}
                            className="text-slate-400 hover:text-red-600 disabled:opacity-50"
                            aria-label={`Remove ${f.name}`}
                          >
                            ✕
                          </button>
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        className={btnSecondary}
                        disabled={triggering}
                        onClick={() => setStaged([])}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Panel>

          {/* Prior uploads — list below */}
          <Panel title="Uploaded transcripts">
            {transcripts.length === 0 ? (
              <p className="text-sm text-slate-500">Nothing uploaded to this domain yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#5A5448] border-b border-[#262420]">
                    <th className="py-1.5 pr-2 font-medium">File</th>
                    <th className="py-1.5 pr-2 font-medium">Size</th>
                    <th className="py-1.5 pr-2 font-medium">By</th>
                    <th className="py-1.5 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {transcripts.map((t) => (
                    <tr key={t.id} className="border-b border-[#1f1e1b]">
                      <td className="py-1.5 pr-2 font-mono">{t.fileName}</td>
                      <td className="py-1.5 pr-2 tabular-nums">
                        {(t.sizeBytes / 1024).toFixed(1)} KB
                      </td>
                      <td className="py-1.5 pr-2 text-slate-500">{t.uploadedByEmail ?? "—"}</td>
                      <td className="py-1.5 text-slate-500">
                        {new Date(t.uploadedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
